// client/src/components/pages/LexiStar/Reports/reportSession.ts
//
// A report page opened with a one-time report token (ST-00188 M4-R2;
// m4-contracts §6). `lexistar-renderer` normally exchanges the token itself at
// POST /api/auth/report-session before it opens the page (the answer's cookies
// carry the session). A page opened as …?print=1&report_session=<token> — a
// renderer configured without the exchange, or a link handed to someone — does
// the exchange itself, before anything else runs (index.tsx mounts the
// chrome-free ReportRenderShell instead of the app): the answer sets the same
// cookies in this browser, and when it also carries a bearer (`jwt`) that is
// sent on this page's API calls only — the interceptor lives exactly as long as
// the render shell, the only thing mounted, and is removed with it. The token
// leaves the address bar straight away (single-use; a reload must not replay it).
//
// Readiness for the renderer: the page root carries data-report-ready="true"
// when it has everything, data-report-error when it cannot (ReportFrame).

import api from '../../../../services/api';
import { isObj } from '../../../../services/lexistarReports';

export const REPORT_SESSION_PARAM = 'report_session';

export interface ReportSession {
  /** Bearer for the page's calls, when the server hands one out (else the cookies carry it). */
  jwt: string | null;
  expires_at: string | null;
  permissions: string[] | null;
  tenant: Record<string, unknown> | null;
  user: Record<string, unknown> | null;
}

let current: ReportSession | null = null;
let interceptorId: number | null = null;

/** The one-time token in `search`, or null. */
export function reportSessionToken(search: string = window.location.search): string | null {
  const t = new URLSearchParams(search).get(REPORT_SESSION_PARAM);
  return t && t.trim() ? t.trim() : null;
}

/** A renderer visit: a LexiStar page with a report-session token in the URL. */
export function isReportRenderUrl(
  loc: { pathname: string; search: string } = window.location
): boolean {
  return loc.pathname.startsWith('/lexistar/') && reportSessionToken(loc.search) !== null;
}

/** POST /auth/report-session — the one-time token for a short-lived session. */
export async function exchangeReportSession(token: string): Promise<ReportSession> {
  const res = await api.post('/auth/report-session', { token });
  const body = isObj(res.data) ? res.data : {};
  const jwt = body.jwt ?? body.access_token;
  if (body.ok !== true && (typeof jwt !== 'string' || !jwt)) {
    throw new Error('The report session could not be opened.');
  }
  return {
    jwt: typeof jwt === 'string' && jwt ? jwt : null,
    expires_at: typeof body.expires_at === 'string' ? body.expires_at : null,
    permissions: Array.isArray(body.permissions) ? body.permissions.map(String) : null,
    tenant: isObj(body.tenant) ? body.tenant : null,
    user: isObj(body.user) ? body.user : null,
  };
}

/** Send `Authorization: Bearer <jwt>` on every call of the shared API client. */
export function installReportSession(session: ReportSession): void {
  clearReportSession();
  current = session;
  if (!session.jwt) return;
  interceptorId = api.interceptors.request.use((config) => {
    if (current?.jwt) config.headers.set('Authorization', `Bearer ${current.jwt}`);
    return config;
  });
}

export function clearReportSession(): void {
  if (interceptorId !== null) api.interceptors.request.eject(interceptorId);
  interceptorId = null;
  current = null;
}

export const reportSessionActive = (): boolean => current !== null;

/** Remove the single-use token from the address bar (keeps every other parameter). */
export function stripReportSessionFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(REPORT_SESSION_PARAM)) return;
    url.searchParams.delete(REPORT_SESSION_PARAM);
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  } catch {
    /* no history API (tests) */
  }
}
